import { useState } from "react";
import styled from "@emotion/styled";
import { useAuth } from "../auth/useAuth";
import { StandardButton } from "../components/StandardButton";
import { addToast } from "../components/Toast";
import { DEVELOPMENT_CONNECTOR_URL, PRODUCTION_CONNECTOR_URL } from "../integrations/claudeConnector";
import { Env, env } from "../utils/environment";
import { colors } from "../utils/style";
import { ApiKeysSection } from "./ApiKeysSection";

const Section = styled.section`
  margin: 18px 0 10px;
`;

const Heading = styled.h2`
  color: ${colors.text.primary};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const SupportingText = styled.p`
  color: ${colors.text.secondary};
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 12px;
`;

const GuideLink = styled.a`
  color: ${colors.text.accent};
`;

const ConnectorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConnectorCard = styled.div<{ $current: boolean }>`
  background: ${colors.bg.secondary};
  border: 1px solid ${(props) => (props.$current ? colors.text.accent : colors.border.secondary)};
  border-radius: 6px;
  padding: 10px;
`;

const ConnectorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ConnectorName = styled.span`
  color: ${colors.text.primary};
  font-size: 14px;
  font-weight: 600;
`;

const CurrentBadge = styled.span`
  color: ${colors.text.accent};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ConnectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ConnectorUrl = styled.code`
  color: ${colors.text.primary};
  background: ${colors.bg.primary};
  border: 1px solid ${colors.border.secondary};
  border-radius: 4px;
  flex: 1;
  font-size: 12px;
  line-height: 18px;
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 6px 8px;
`;

const CopyButton = styled(StandardButton)`
  flex-shrink: 0;
  font-size: 13px;
  padding: 6px 8px;
`;

interface ConnectorOption {
  label: string;
  url: string;
  current: boolean;
}

export function ConnectAiSection() {
  const { user } = useAuth();
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const developmentIsCurrent = env !== Env.prod;
  const production: ConnectorOption = {
    label: "Ideaflow",
    url: PRODUCTION_CONNECTOR_URL,
    current: !developmentIsCurrent,
  };
  const development: ConnectorOption = {
    label: "Ideaflow (dev)",
    url: DEVELOPMENT_CONNECTOR_URL,
    current: developmentIsCurrent,
  };
  const connectors = developmentIsCurrent ? [development, production] : [production, development];

  const copyConnectorUrl = async (connector: ConnectorOption) => {
    try {
      await navigator.clipboard.writeText(connector.url);
      setCopiedUrl(connector.url);
      addToast({ type: "info", content: `${connector.label} connector URL copied` });
      window.setTimeout(() => setCopiedUrl(null), 2000);
    } catch {
      addToast({ type: "warning", content: "Could not copy connector URL" });
    }
  };

  return (
    <Section aria-labelledby="connect-ai-heading">
      <Heading id="connect-ai-heading">Connect AI / API access</Heading>
      <SupportingText>
        Connect Claude or ChatGPT to your Ideaflow notes. For guided setup, open the{" "}
        <GuideLink href="https://ideaflow.app/connect" target="_blank" rel="noopener noreferrer">
          Ideaflow connector guide
        </GuideLink>
        .
      </SupportingText>

      <ConnectorList>
        {connectors.map((connector) => (
          <ConnectorCard key={connector.url} $current={connector.current}>
            <ConnectorHeader>
              <ConnectorName>{connector.label}</ConnectorName>
              {connector.current && <CurrentBadge>Current environment</CurrentBadge>}
            </ConnectorHeader>
            <ConnectorRow>
              <ConnectorUrl>{connector.url}</ConnectorUrl>
              <CopyButton
                aria-label={`Copy ${connector.label} connector URL`}
                onClick={() => copyConnectorUrl(connector)}
              >
                {copiedUrl === connector.url ? "Copied" : "Copy connector URL"}
              </CopyButton>
            </ConnectorRow>
          </ConnectorCard>
        ))}
      </ConnectorList>

      {user?.isIdeaflowId ? (
        <div>
          <Heading>API keys</Heading>
          <SupportingText>
            API key management is temporarily unavailable with Ideaflow ID on develop. Existing keys are unchanged.
          </SupportingText>
        </div>
      ) : (
        <ApiKeysSection />
      )}
    </Section>
  );
}
